import { useEffect, useState } from "react";
import {
  Button,
  Empty,
  Input,
  Pagination,
  Segmented,
  Select,
  Skeleton,
} from "antd";
import {
  AppstoreOutlined,
  BarsOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { publicApi } from "../services/api";
import { WorkCard, QueryError } from "../components/Editorial";
import { PeriodPicker } from "../components/PeriodPicker";
import { SEOHead } from "../components/SEOHead";
import { RATING_OPTIONS } from "../components/RatingDisplay";
import { integerParam, readPeriod } from "../utils/period";

export function WorksListPage() {
  const [params, setParams] = useSearchParams();
  const { year, month } = readPeriod(params);
  const type = ["book", "movie", "series"].includes(params.get("type") ?? "")
    ? params.get("type")!
    : undefined;
  const page = integerParam(params.get("page"), 1, 100000) ?? 1;
  const rating = integerParam(params.get("rating"), 1, 8);
  const search = params.get("search") ?? "";
  const [draft, setDraft] = useState(search);
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem("mordeczka_library_view") === "list"
        ? "list"
        : "grid";
    } catch {
      return "grid";
    }
  });
  useEffect(() => {
    setDraft(search);
  }, [search]);
  const update = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    next.delete("page");
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { preventScrollReset: true });
  };
  const years = useQuery({
    queryKey: ["public-library-years"],
    queryFn: publicApi.listYears,
  });
  const query = useQuery({
    queryKey: ["public-library", year, month, type, rating, search, page, 24],
    queryFn: () =>
      publicApi.listLibrary({
        year,
        month,
        type,
        rating,
        search: search || undefined,
        page,
        page_size: 24,
      }),
  });
  return (
    <div className="library-page">
      <SEOHead
        title="Biblioteka"
        description="Przeczytane książki, obejrzane filmy i sezony seriali. Przeglądaj osobistą bibliotekę według dat, typów i ocen."
        canonicalPath="/library"
      />
      <div className="page-intro">
        <span className="eyebrow">Na mojej półce</span>
        <h1>Biblioteka</h1>
        <p>Historie już poznane. Niektóre na chwilę, inne na zawsze.</p>
      </div>
      <div className="library-filters">
        <div className="filter-row">
          <Segmented
            aria-label="Typ dzieła"
            value={type ?? "all"}
            onChange={(value) =>
              update({ type: value === "all" ? undefined : String(value) })
            }
            options={[
              { value: "all", label: "Wszystkie" },
              { value: "book", label: "Książki" },
              { value: "movie", label: "Filmy" },
              { value: "series", label: "Seriale" },
            ]}
          />
          <Segmented
            aria-label="Układ wyników"
            value={view}
            onChange={(value) => {
              setView(String(value));
              try {
                localStorage.setItem("mordeczka_library_view", String(value));
              } catch {
                /* Optional preference. */
              }
            }}
            options={[
              {
                value: "grid",
                label: (
                  <span>
                    <AppstoreOutlined aria-hidden="true" /> Siatka
                  </span>
                ),
              },
              {
                value: "list",
                label: (
                  <span>
                    <BarsOutlined aria-hidden="true" /> Lista
                  </span>
                ),
              },
            ]}
          />
        </div>
        <div className="filter-row">
          <PeriodPicker
            year={year}
            month={month}
            years={years.data?.years ?? []}
            onChange={(y, m) =>
              update({
                year: y === undefined ? "all" : String(y),
                month: m ? String(m) : undefined,
              })
            }
          />
          <label className="rating-filter">
            <span>Ocena</span>
            <Select
              aria-label="Ocena"
              allowClear
              placeholder="Wszystkie oceny"
              value={rating}
              onChange={(value) =>
                update({ rating: value ? String(value) : undefined })
              }
              options={RATING_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
            />
          </label>
          <form
            className="library-search"
            onSubmit={(event) => {
              event.preventDefault();
              update({ search: draft.trim() || undefined });
            }}
          >
            <label htmlFor="library-search">Szukaj tytułu</label>
            <div>
              <Input
                id="library-search"
                prefix={<SearchOutlined />}
                placeholder="Tytuł lub tytuł oryginalny"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                allowClear
              />
              <Button htmlType="submit">Szukaj</Button>
            </div>
          </form>
        </div>
      </div>
      {years.isError && (
        <QueryError
          message="Nie udało się pobrać listy lat."
          retry={() => void years.refetch()}
        />
      )}
      {query.isError ? (
        <QueryError retry={() => void query.refetch()} />
      ) : query.isPending ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <>
          <div className="results-heading">
            <span>{query.data.total.toLocaleString("pl-PL")} pozycji</span>
            <span>Od najnowszych ukończeń</span>
          </div>
          {query.data.items.length ? (
            <div
              className={
                view === "grid" ? "work-grid library-grid" : "work-list"
              }
            >
              {query.data.items.map((entry) => (
                <WorkCard
                  key={entry.id}
                  entry={entry}
                  compact={view === "list"}
                />
              ))}
            </div>
          ) : (
            <Empty description="Brak pozycji dla wybranych filtrów." />
          )}
          {query.data.total > 24 && (
            <Pagination
              className="library-pagination"
              current={page}
              total={query.data.total}
              pageSize={24}
              showSizeChanger={false}
              onChange={(value) => update({ page: String(value) })}
            />
          )}
        </>
      )}
    </div>
  );
}
